/**
 * sanitizeTestimonial.js — defensive display-side shield for federated
 * testimonial fields.
 *
 * WHY THIS EXISTS
 * ───────────────
 * On 2026-07-08 JEC's upstream `/api/v1/public/testimonials` began
 * returning `Enc:<fernet-base64>` in the `city` field instead of
 * plaintext. JEC has since deployed a server-side decryption fix so
 * the field ships as a readable string ("Fussa", "APO", "") going
 * forward. This shield remains anyway as belt-and-suspenders:
 * defense against any future upstream contract drift, cache-miss
 * during rollouts, or similar defects from other federated
 * providers.
 *
 * DESIGN CONTRACT
 * ───────────────
 * Pure function. Never mutates upstream data, never calls any API,
 * never touches storage. Given a possibly-corrupted or possibly-
 * encrypted value, returns either the original readable string or
 * null so the rendering component can suppress just that component
 * without collapsing the whole location row.
 *
 * SECURITY POSTURE
 * ────────────────
 * The `Enc:` string that was publicly rendered is a Fernet-format
 * ciphertext; no direct plaintext, key material, or immediately-
 * exploitable condition has been identified from the exposed
 * ciphertext. This is NOT a confirmed zero-risk condition absent a
 * full JEC-backend implementation review. This shield is a display
 * hygiene control, not a cryptographic control.
 */

/**
 * Return true when a testimonial field's value should NOT be shown.
 *
 * Contract MUST stay identical to the backend twin at
 * `backend/services/testimonial_display_shield.py::is_unrenderable`
 * so the belt+suspenders guarantee actually holds — if the backend
 * shield were ever bypassed, the frontend catches the exact same
 * set of bad values.
 *
 * Currently catches:
 *   • null / undefined / any non-string value
 *   • Empty / whitespace-only strings
 *   • Explicit placeholder tokens ("null", "undefined", "n/a",
 *     "none", "-") — case-insensitive
 *   • JEC's `Enc:` encrypted-payload prefix (case-insensitive)
 */
export function isUnrenderable(v) {
  if (v == null) return true;
  if (typeof v !== "string") return true;
  const trimmed = v.trim();
  if (trimmed.length === 0) return true;
  const lower = trimmed.toLowerCase();
  if (
    lower === "null" ||
    lower === "undefined" ||
    lower === "n/a" ||
    lower === "none" ||
    lower === "-" ||
    // JEC v1 partner endpoint has historically shipped literal
    // "Unavailable" (or "unavailable") whenever the source record's
    // display_name field is missing or opted-out of publication.
    // Treat as a null placeholder so downstream fallbacks fire.
    lower === "unavailable" ||
    lower === "name unavailable" ||
    lower === "anonymous"
  ) return true;
  if (lower.startsWith("enc:")) return true;
  return false;
}

/**
 * Return the string if it's safe to render; otherwise return null.
 * Callers use `sanitizeField(item.city) && <span>…</span>` — the null
 * short-circuits the whole rendered row.
 */
export function sanitizeField(v) {
  return isUnrenderable(v) ? null : v;
}
